import React, { useMemo } from "react";
import { Card, Empty, Select, Typography } from "antd";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";

const { Option } = Select;
const { Text } = Typography;

const CHART_COLORS = {
  views: "#1890ff",
  clicks: "#52c41a",
  conversions: "#722ed1",
  revenue: "#fa8c16",
  ctr: "#13c2c2",
  conversionRate: "#eb2f96",
};

const PromotionPerformanceChart = ({
  title = "Performance Over Time",
  data = [],
  chartType = "line", // 'line', 'area', 'bar'
  metrics = ["views", "clicks"], // array of metrics to show
  xAxisKey = "date",
  loading = false,
  height = 350,
  showLegend = true,
  onChartTypeChange,
  formatXAxis,
  formatTooltip,
  style = {},
}) => {
  // Format data labels
  const formatMetricLabel = (metric) => {
    const labels = {
      views: "Views",
      clicks: "Clicks",
      conversions: "Conversions",
      revenue: "Revenue",
      ctr: "CTR (%)",
      conversionRate: "Conv. Rate (%)",
      uniqueViews: "Unique Views",
    };
    return labels[metric] || metric;
  };

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <div
        style={{
          background: "rgba(255, 255, 255, 0.98)",
          padding: "12px 16px",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Text strong style={{ display: "block", marginBottom: 8 }}>
          {formatTooltip ? formatTooltip(label) : label}
        </Text>
        {payload.map((entry, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: 4,
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <Text type="secondary" style={{ marginRight: 8 }}>
              {formatMetricLabel(entry.dataKey)}:
            </Text>
            <Text strong>
              {entry.dataKey === "revenue"
                ? `$${entry.value?.toLocaleString() || 0}`
                : entry.dataKey === "ctr" || entry.dataKey === "conversionRate"
                ? `${entry.value?.toFixed(2) || 0}%`
                : entry.value?.toLocaleString() || 0}
            </Text>
          </div>
        ))}
      </div>
    );
  };

  // Render chart based on type
  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    const xAxisProps = {
      dataKey: xAxisKey,
      tick: { fill: "#8c8c8c", fontSize: 12 },
      tickFormatter: formatXAxis,
      axisLine: { stroke: "#f0f0f0" },
      tickLine: false,
    };

    const yAxisProps = {
      tick: { fill: "#8c8c8c", fontSize: 12 },
      axisLine: { stroke: "#f0f0f0" },
      tickLine: false,
      tickFormatter: (value) =>
        value >= 1000 ? `${(value / 1000).toFixed(1)}k` : value,
    };

    const legendProps = showLegend
      ? {
          formatter: formatMetricLabel,
          iconType: "circle",
          iconSize: 10,
        }
      : false;

    switch (chartType) {
      case "area":
        return (
          <AreaChart {...commonProps}>
            <defs>
              {metrics.map((metric) => (
                <linearGradient
                  key={metric}
                  id={`gradient-${metric}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={CHART_COLORS[metric] || "#1890ff"}
                    stopOpacity={0.3}
                  />
                  <stop
                    offset="95%"
                    stopColor={CHART_COLORS[metric] || "#1890ff"}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend {...legendProps} />}
            {metrics.map((metric) => (
              <Area
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={CHART_COLORS[metric] || "#1890ff"}
                fill={`url(#gradient-${metric})`}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 2 }}
              />
            ))}
          </AreaChart>
        );

      case "bar":
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend {...legendProps} />}
            {metrics.map((metric) => (
              <Bar
                key={metric}
                dataKey={metric}
                fill={CHART_COLORS[metric] || "#1890ff"}
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            ))}
          </BarChart>
        );

      case "line":
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis {...xAxisProps} />
            <YAxis {...yAxisProps} />
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend {...legendProps} />}
            {metrics.map((metric) => (
              <Line
                key={metric}
                type="monotone"
                dataKey={metric}
                stroke={CHART_COLORS[metric] || "#1890ff"}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        );
    }
  };

  if (!data || data.length === 0) {
    return (
      <Card
        title={title}
        bordered={false}
        loading={loading}
        style={style}
        extra={
          onChartTypeChange && (
            <Select
              value={chartType}
              onChange={onChartTypeChange}
              style={{ width: 100 }}
              size="small"
            >
              <Option value="line">Line</Option>
              <Option value="area">Area</Option>
              <Option value="bar">Bar</Option>
            </Select>
          )
        }
      >
        <Empty
          description="No data available for the selected period"
          style={{ padding: "40px 0" }}
        />
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={title}
        bordered={false}
        loading={loading}
        style={style}
        extra={
          onChartTypeChange && (
            <Select
              value={chartType}
              onChange={onChartTypeChange}
              style={{ width: 100 }}
              size="small"
            >
              <Option value="line">Line</Option>
              <Option value="area">Area</Option>
              <Option value="bar">Bar</Option>
            </Select>
          )
        }
      >
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </Card>
    </motion.div>
  );
};

export default PromotionPerformanceChart;
